import { useState, useEffect, useRef, useCallback } from 'react';

const API = 'https://bot.devit.es/api';
function getToken() { return localStorage.getItem('devit_token'); }
function authHeaders(extra = {}) {
  return { 'Authorization': `Bearer ${getToken()}`, ...extra };
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const VISTAS = {
  pequeno: { label: '▪ Pequeño', minWidth: 100, gap: 10, fontNombre: 11, fontMeta: 10, mostrarMeta: false, botonTexto: false },
  mediano: { label: '▪▪ Mediano', minWidth: 150, gap: 14, fontNombre: 12, fontMeta: 11, mostrarMeta: true, botonTexto: true },
  grande:  { label: '▪▪▪ Grande', minWidth: 220, gap: 18, fontNombre: 13, fontMeta: 11, mostrarMeta: true, botonTexto: true },
};

export default function Imagenes() {
  const [imagenes, setImagenes]   = useState([]);
  const [cuota, setCuota]         = useState({ uso_mb: 0, limite_mb: 200, libre_mb: 200 });
  const [loading, setLoading]     = useState(true);
  const [subiendo, setSubiendo]   = useState(false);
  const [progreso, setProgreso]   = useState(null); // { actual, total }
  const [renombrando, setRenombrando] = useState(null); // nombre siendo editado
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [msg, setMsg]             = useState(null);
  const [vista, setVista] = useState(() => localStorage.getItem('devit_imagenes_vista') || 'mediano');
  const fileRef = useRef();

  function cambiarVista(v) {
    setVista(v);
    localStorage.setItem('devit_imagenes_vista', v);
  }
  const cfg = VISTAS[vista];

  const cargar = useCallback(async () => {
    try {
      const r = await fetch(`${API}/tenants/media/`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setImagenes(d.imagenes || []);
        setCuota({ uso_mb: d.uso_mb, limite_mb: d.limite_mb, libre_mb: d.libre_mb });
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  async function subirUnaImagen(file) {
    const b64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const r = await fetch(`${API}/tenants/media/subir`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ file_b64: b64, nombre_original: file.name }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail || 'Error subiendo imagen');
    return d;
  }

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setSubiendo(true);
    setMsg(null);
    let ok = 0, fallos = [];
    for (let i = 0; i < files.length; i++) {
      setProgreso({ actual: i + 1, total: files.length });
      try {
        await subirUnaImagen(files[i]);
        ok++;
      } catch (err) {
        fallos.push(`${files[i].name}: ${err.message}`);
      }
    }
    setProgreso(null);
    setSubiendo(false);
    fileRef.current.value = '';
    await cargar();
    if (fallos.length === 0) {
      setMsg({ type: 'ok', text: `✅ ${ok} imagen${ok !== 1 ? 'es' : ''} subida${ok !== 1 ? 's' : ''} correctamente.` });
    } else {
      setMsg({ type: 'err', text: `${ok} subidas OK. Errores: ${fallos.join(' · ')}` });
    }
  }

  async function handleDelete(nombre) {
    if (!window.confirm(`¿Borrar "${nombre}"? Si algún post programado la usa, dejará de mostrarse.`)) return;
    await fetch(`${API}/tenants/media/${encodeURIComponent(nombre)}`, { method: 'DELETE', headers: authHeaders() });
    cargar();
  }

  function empezarRenombrar(img) {
    setRenombrando(img.nombre);
    const sinExt = img.nombre.includes('.') ? img.nombre.slice(0, img.nombre.lastIndexOf('.')) : img.nombre;
    setNuevoNombre(sinExt);
  }

  async function confirmarRenombrar(nombre) {
    if (!nuevoNombre.trim()) { setRenombrando(null); return; }
    try {
      const r = await fetch(`${API}/tenants/media/${encodeURIComponent(nombre)}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ nuevo_nombre: nuevoNombre.trim() }),
      });
      const d = await r.json();
      if (r.ok) {
        setMsg({ type: 'ok', text: `✅ Renombrada a "${d.nombre}". Los posts que ya la usaban se actualizaron automáticamente.` });
      } else {
        setMsg({ type: 'err', text: d.detail || 'Error renombrando' });
      }
    } catch {
      setMsg({ type: 'err', text: 'Error de conexión' });
    }
    setRenombrando(null);
    cargar();
  }

  const pct = cuota.limite_mb ? Math.min(100, (cuota.uso_mb / cuota.limite_mb) * 100) : 0;

  if (loading) return <div className="content" style={{ color: 'var(--text3)' }}>Cargando...</div>;

  return (
    <div>
      <div className="topbar">
        <div>
          <div className="page-title">Imágenes</div>
          <div className="page-sub">Galería de imágenes para tus publicaciones</div>
        </div>
      </div>

      <div className="content">
        {/* Cuota */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ color: 'var(--text2)', fontSize: 13, fontWeight: 600 }}>Espacio usado</span>
            <span style={{ color: pct > 80 ? '#f87171' : 'var(--text3)', fontSize: 13 }}>
              {cuota.uso_mb} MB / {cuota.limite_mb} MB · {cuota.libre_mb} MB libres
            </span>
          </div>
          <div style={{ height: 6, background: 'var(--bg2)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: pct > 80 ? '#f87171' : '#7c3aed', transition: 'width 0.3s' }} />
          </div>
        </div>

        {/* Subida */}
        <div className="card" style={{ marginBottom: 20 }}>
          <input ref={fileRef} type="file" accept="image/*" multiple
            style={{ display: 'none' }} onChange={handleFiles} />
          <button className="btn btn-primary" onClick={() => fileRef.current.click()} disabled={subiendo}>
            {subiendo ? `⏳ Subiendo ${progreso?.actual || 0}/${progreso?.total || 0}...` : '📤 Subir imágenes'}
          </button>
          <span style={{ marginLeft: 12, color: 'var(--text3)', fontSize: 12 }}>
            Puedes seleccionar varias a la vez. Máx 5MB por imagen.
          </span>
          {msg && (
            <div style={{
              marginTop: 14, padding: '10px 14px', borderRadius: 8, fontSize: 13,
              background: msg.type === 'ok' ? '#064e3b22' : '#7f1d1d22',
              color: msg.type === 'ok' ? '#34d399' : '#f87171',
              border: `1px solid ${msg.type === 'ok' ? '#34d39944' : '#f8717144'}`,
            }}>
              {msg.text}
            </div>
          )}
        </div>

        {/* Galería */}
        {imagenes.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--text3)', padding: 40 }}>
            No tienes imágenes subidas aún. Sube la primera.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ color: 'var(--text3)', fontSize: 13 }}>{imagenes.length} imágenes</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {Object.entries(VISTAS).map(([key, v]) => (
                  <button key={key} onClick={() => cambiarVista(key)} style={{
                    padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    background: vista === key ? 'var(--accent)' : 'var(--bg2)',
                    color: vista === key ? '#fff' : 'var(--text3)',
                    border: `1px solid ${vista === key ? 'var(--accent)' : 'var(--border)'}`,
                  }}>{v.label}</button>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill,minmax(${cfg.minWidth}px,1fr))`, gap: cfg.gap }}>
              {imagenes.map(img => (
                <div key={img.nombre} className="card" style={{ padding: vista === 'pequeno' ? 6 : 10 }}>
                  <div style={{
                    width: '100%', aspectRatio: '1', borderRadius: 8, overflow: 'hidden',
                    background: 'var(--bg2)', marginBottom: vista === 'pequeno' ? 6 : 10,
                  }}>
                    <img src={img.url} alt={img.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>

                  {renombrando === img.nombre ? (
                    <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                      <input className="form-input" style={{ fontSize: 12, padding: '4px 8px' }}
                        value={nuevoNombre} onChange={e => setNuevoNombre(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && confirmarRenombrar(img.nombre)}
                        autoFocus />
                      <button onClick={() => confirmarRenombrar(img.nombre)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#34d399' }}>✓</button>
                      <button onClick={() => setRenombrando(null)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)' }}>✕</button>
                    </div>
                  ) : (
                    <div title={img.nombre} style={{
                      color: 'var(--text1)', fontSize: cfg.fontNombre, fontWeight: 600, marginBottom: vista === 'pequeno' ? 4 : 6,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {img.nombre}
                    </div>
                  )}

                  {cfg.mostrarMeta && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text3)', fontSize: cfg.fontMeta, marginBottom: 8 }}>
                      <span>{formatBytes(img.size)}</span>
                      <span>{new Date(img.modificado_en * 1000).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: vista === 'pequeno' ? 4 : 6, marginTop: cfg.mostrarMeta ? 0 : 6 }}>
                    <button onClick={() => empezarRenombrar(img)} title="Renombrar"
                      style={{ flex: 1, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 0', cursor: 'pointer', color: 'var(--text2)', fontSize: 11 }}>
                      {cfg.botonTexto ? '✏️ Renombrar' : '✏️'}
                    </button>
                    <button onClick={() => handleDelete(img.nombre)} title="Borrar"
                      style={{ flex: 1, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 0', cursor: 'pointer', color: '#f87171', fontSize: 11 }}>
                      {cfg.botonTexto ? '🗑️ Borrar' : '🗑️'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
